/* eslint-disable camelcase */
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import { openWindow } from 'services/inAppBrowser';

import useFeatureToggle from 'hooks/useFeatureToggle';

import { isApp } from 'services/environment';

const QUERY = gql`
  query GetRecipeShopLink(
    $id: String
    $token: String
    $platform: Platform
    $preferred_vendor: String
    $country: String
  ) {
    Recipe(id: $id, token: $token) {
      id
      ingredient_shop_button(
        country: $country
        platform: $platform
        preferred_vendor: $preferred_vendor
      ) {
        height
        image_url
        params
        text
        type
        url
        width
      }
    }
  }
`;

function ShopForIngredients(props) {
  const { recipeId, className, redirect, vendor, country, platform } = props;
  const formRef = useRef(null);
  const baseUrl = useSelector(state => state.config.baseUrl);
  const [enabled] = useFeatureToggle('ingredientShopLink');

  // If the user has an override on their geolocation then we will send authentication so that the server can validate this.
  // Otherwise we don't send auth so that the request can be served from the cache
  const usingLocationOverride = useSelector(state =>
    state.user.permissions?.some(p => p.startsWith('geoip:')),
  );
  const userSelectedVendor = useSelector(
    state => vendor || state.user.settings?.ingredient_shop_vendor,
  );

  const isCorporate = useSelector(state => state.user.geolocation.corporate);
  const isLoggedIn = useSelector(state => state.user.logged_in);

  const userHomeCountry = useSelector(state => country || state.user.home_country);

  const isNotSignedCorporate = useMemo(() => isCorporate && !isLoggedIn, [isCorporate, isLoggedIn]);

  const { data } = useQuery(QUERY, {
    variables: {
      id: recipeId,
      token: null, // Token not required for this field
      platform,
      country: userHomeCountry,
      preferred_vendor: userSelectedVendor,
      _authenticated: usingLocationOverride,
    },
    skip: isNotSignedCorporate || !enabled || !userSelectedVendor || userSelectedVendor === 'none',
    ssr: false,
  });

  const { height, text, url, width, type, params, image_url } =
    data?.Recipe?.ingredient_shop_button || {};

  useEffect(() => {
    if (type === 'form' && redirect && formRef.current) {
      formRef.current.submit();
    }
  });

  const handleAppButtonClick = () => {
    openWindow(
      `${baseUrl}/recipe/${recipeId}/_groceries?country=${userHomeCountry}&vendor=${userSelectedVendor}`,
      '_system',
    );
  };

  if (!data?.Recipe?.ingredient_shop_button?.url) {
    return null;
  }

  if (type === 'iframe') {
    return (
      <iframe
        className={className}
        title={text}
        src={url}
        scrolling="no"
        frameBorder="0"
        style={{
          width,
          height,
        }}
      />
    );
  } else if (type === 'form') {
    const buttonContent = image_url ? (
      <img src={image_url} width={width} height={height} alt={text} />
    ) : (
      text
    );
    if (isApp()) {
      return (
        <div className={className}>
          <button className="link--underline" onClick={handleAppButtonClick}>
            {buttonContent}
          </button>
        </div>
      );
    }
    const fields = JSON.parse(params);
    return (
      <form
        ref={formRef}
        method="post"
        action={url}
        className={className}
        target={redirect ? null : '_blank'}
      >
        {Object.keys(fields).map(name => (
          <input type="hidden" key={name} name={name} value={fields[name]} />
        ))}
        <button className="link--underline" type="submit" hidden={redirect}>
          {buttonContent}
        </button>
      </form>
    );
  }
  return null;
}

ShopForIngredients.propTypes = {
  recipeId: PropTypes.string.isRequired,
  className: PropTypes.string,
  redirect: PropTypes.bool,
  vendor: PropTypes.string,
  country: PropTypes.string,
  platform: PropTypes.string,
};

ShopForIngredients.defaultProps = {
  className: null,
  redirect: false,
  vendor: null,
  country: null,
  platform: isApp() ? 'app' : 'web',
};

export default ShopForIngredients;
